import { useState } from "react";
import { CheckCircle2, Loader2, Lock, Mic, Sparkles, TrendingUp } from "lucide-react";
import { DEMO_QUESTIONS, demoUsed, gradeAnswer, markDemoUsed, savedDemo } from "@/lib/interviewDemo";

const Scores = ({ r }) => (
  <div className="space-y-2.5">
    {r.scores?.map((s, i) => (
      <div key={s.label}>
        <div className="mb-1 flex justify-between text-xs"><span className="text-zinc-400">{s.label}</span><span className="font-mono text-zinc-100">{s.value}</span></div>
        <div className="h-1.5 overflow-hidden rounded-full bg-[#27272A]"><div className="lp-fill h-full rounded-full bg-gradient-to-r from-[#D4AF37] to-[#F3C649]" style={{ width: `${s.value}%`, animationDelay: `${i * 150}ms` }} /></div>
      </div>
    ))}
  </div>
);

const Result = ({ r, onCta }) => (
  <div className="fade-up space-y-4" data-testid="demo-result">
    <div className="rounded-xl border border-[#27272A] bg-[#09090B]/60 p-3 text-xs text-zinc-400"><span className="text-[#D4AF37]">Q:</span> {r.question}</div>
    <div className="grid gap-4 sm:grid-cols-[1fr_1.2fr]">
      <div className="rounded-xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#33290D] to-[#141108] p-4">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">{r.ai ? "AI feedback" : "Instant preview"}</span>
          {r.grade && <span className="font-mono text-2xl font-bold text-[#F3C649]" data-testid="demo-grade">{r.grade}</span>}
        </div>
        <Scores r={r} />
      </div>
      <div className="space-y-2.5 text-sm">
        {r.strengths?.map((s) => <p key={s} className="flex gap-2 text-zinc-300"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />{s}</p>)}
        {r.improve?.map((s) => <p key={s} className="flex gap-2 text-zinc-300"><TrendingUp className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" />{s}</p>)}
        {r.tip && <p className="rounded-lg bg-[#18181B] p-2.5 text-xs text-zinc-400"><span className="text-[#F3C649]">Tip:</span> {r.tip}</p>}
        {!r.ai && <p className="text-[11px] text-zinc-500">Quick rule-based preview. Full AI coaching is waiting inside.</p>}
      </div>
    </div>
    <button onClick={onCta} className="flex w-full items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-5 py-3 text-sm font-semibold text-black transition-colors hover:bg-[#F3C649]" data-testid="demo-signup-button">
      <Lock className="h-4 w-4" />Unlock unlimited mock interviews, free for 7 days
    </button>
  </div>
);

export const InterviewDemo = ({ onCta }) => {
  const [done, setDone] = useState(() => (demoUsed() ? savedDemo() : null));
  const [q, setQ] = useState(null);
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const r = { ...(await gradeAnswer(q, answer.trim())), question: q };
      markDemoUsed(r);
      setDone(r);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="fade-up relative overflow-hidden rounded-2xl border border-[#D4AF37]/30 bg-[#121214]/90 p-6 shadow-[0_10px_35px_rgba(212,175,55,0.08)] backdrop-blur-xl sm:col-span-2" data-testid="interview-demo">
      <div className="pointer-events-none absolute -left-16 -top-16 h-44 w-44 rounded-full bg-[#D4AF37]/10 blur-3xl" />
      <div className="mb-4 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#D4AF37]/40 bg-[#33290D] text-[#F3C649]"><Mic className="h-5 w-5" /></span>
        <div>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#F3C649]">Try it free · one question</span>
          <h3 className="font-display text-lg text-zinc-100">Practice an interview answer</h3>
        </div>
      </div>
      {done ? (done.question && done.scores ? <Result r={done} onCta={onCta} /> : null) : !q ? (
        <div className="grid gap-2 sm:grid-cols-2" data-testid="demo-questions">
          {DEMO_QUESTIONS.map((x, i) => (
            <button key={x} onClick={() => setQ(x)} data-testid={`demo-question-${i}`}
              className="rounded-xl border border-[#27272A] bg-[#18181B] p-3.5 text-left text-sm text-zinc-200 transition-[border-color,transform] hover:-translate-y-0.5 hover:border-[#D4AF37]/50">
              {x}
            </button>
          ))}
        </div>
      ) : (
        <div className="fade-up space-y-3">
          <div className="flex items-start justify-between gap-3 rounded-xl border border-[#27272A] bg-[#18181B] p-3.5 text-sm text-zinc-100">
            <span data-testid="demo-selected-question">{q}</span>
            <button onClick={() => { setQ(null); setErr(""); }} className="shrink-0 text-xs text-zinc-500 hover:text-[#F3C649]" data-testid="demo-change-question">Change</button>
          </div>
          <textarea value={answer} onChange={(e) => setAnswer(e.target.value.slice(0, 1500))} rows={5} placeholder="Type your answer as if you're in the interview…"
            className="w-full resize-none rounded-xl border border-[#27272A] bg-[#09090B] p-3.5 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-[#D4AF37]/60 focus:outline-none" data-testid="demo-answer-input" />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-zinc-500">{answer.trim().split(/\s+/).filter(Boolean).length} words · aim for 60–200</span>
            <button onClick={submit} disabled={busy || answer.trim().length < 40} data-testid="demo-submit-button"
              className="flex items-center gap-2 rounded-full bg-[#D4AF37] px-5 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-[#F3C649] disabled:opacity-40">
              {busy ? <><Loader2 className="h-4 w-4 animate-spin" />Coaching your answer…</> : <><Sparkles className="h-4 w-4" />Get my feedback</>}
            </button>
          </div>
          {err && <p className="text-xs text-red-400" data-testid="demo-error">{err}</p>}
        </div>
      )}
    </div>
  );
};
